import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import UpcomingEvents from './UpcomingEvents';
import NotificationsCard from './NotificationsCard';
import Catalog from './Catalog';
import AiMediaLaunchCard from '@/components/AiMedia/LaunchCard';
import PageContainer from '@/components/Container/PageContainer';
import { Button } from '@/components/UI/Button';
import { H4 } from '@/components/UI/Typography/Heading';
import {
  AccountVerifiedBadge,
  CreditReportBadge,
  ECertBadge,
  IdentificationCheckBadge,
  SupplementalInfoBadge,
  BRBadge,
} from '@/components/UI/Badge';

import { HomeWrapper, Notice, Icons, ExpiryDateNotification } from './style';
import { DealsChart, DocumentsChart, MembersChart, SubscriptionChart } from './Charts';
import { Flex } from '@/layout';
import useResize from '@/hooks/useResize';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import PblOcCitizenRegistration from '@/pages/Pbl/OcCitizenRegistration';
import PblCitizenCaseApplication from '@/pages/Pbl/CitizenCaseApplication';
import PblLawyerManageCases from '@/pages/Pbl/LawyerManageCases';
import PATH from '@/router/path';
import { v2UiEnabled } from '@/components/DmpV2/features';
import { resolvePblHomeDashboard } from './pblHomeDashboard';

const Home = () => {
  // @ts-ignore
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResize();
  const [showNewFeature, setShowNewFeature] = useState(true);
  const [isExpiring, setIsExpiring] = useState(false);
  const { activeApplication, userData } = useAppSelector(getAuthSelector);
  const pblDashboard = resolvePblHomeDashboard(userData, activeApplication);

  useEffect(() => {
    if (pblDashboard !== 'standard_home') return;
    if (userData?.plan?.expiration_date) {
      const expiryDate = new Date(userData.plan.expiration_date);
      // 確認過期日是否在一個月內
      const now = new Date();
      const diff = expiryDate.getTime() - now.getTime();
      const days = diff / (1000 * 60 * 60 * 24);
      if (days < 30) {
        setIsExpiring(true);
      }
    }
  }, [pblDashboard, userData?.plan?.expiration_date]);

  if (pblDashboard === 'pbl_dashboard') return <Navigate to={PATH.pbl.dashboard} replace />;
  if (pblDashboard === 'oc_authorization_form') return <PblOcCitizenRegistration />;
  if (pblDashboard === 'citizen_case_application') return <PblCitizenCaseApplication />;
  if (pblDashboard === 'lawyer_manage_cases') return <PblLawyerManageCases />;
  if (pblDashboard === 'lawyer_application_status') {
    return <Navigate to={PATH.pbl.lawyerApplicationStatus} replace />;
  }

  return (
    <PageContainer title={''}>
      {isExpiring && (
        <Notice isTablet={isTablet} isMobile={isMobile}>
          <H4 className="text">{t('pages.home.expriedNotice')}</H4>
          <Link to="/profile/subscriptionDetail">
            <Button>{t('buttons.go')}</Button>
          </Link>
        </Notice>
      )}
      {isTablet && (
        <Notice isTablet={isTablet} isMobile={isMobile}>
          <H4 className="text">{t('pages.home.sizeRecommend')}</H4>
        </Notice>
      )}
      <Notice isTablet={isTablet} isMobile={isMobile}>
        <Icons>
          <BRBadge width={24} align={'center'} />
          <SupplementalInfoBadge width={24} align={'center'} />
          <IdentificationCheckBadge width={24} align={'center'} />
          <ECertBadge width={24} align={'center'} />
          {/* <CreditReportBadge width={24} align={'center'} /> */}
          <AccountVerifiedBadge width={24} align={'center'} />
        </Icons>
        <H4 className="text">{t('pages.home.filling')}</H4>
        <Link to="/profile/edit">
          <Button>{t('buttons.go')}</Button>
        </Link>
      </Notice>
      <HomeWrapper isTablet={isTablet} isMobile={isMobile}>
        <Flex flexDirection={isMobile ? 'column' : 'row'} maxWidth={1215} width={'100%'} gridGap={15}>
          <UpcomingEvents />
          <NotificationsCard />
        </Flex>
      </HomeWrapper>
      <HomeWrapper isTablet={isTablet} isMobile={isMobile}>
        <Catalog />
      </HomeWrapper>
      {v2UiEnabled() && <HomeWrapper isTablet={isTablet} isMobile={isMobile}><AiMediaLaunchCard /></HomeWrapper>}
      {showNewFeature && (
        <HomeWrapper isTablet={isTablet} isMobile={isMobile}>
          <Flex flexDirection={'column'} gridGap={15}>
            <DealsChart />
            <MembersChart />
          </Flex>
          {!v2UiEnabled() && <SubscriptionChart />}
          <DocumentsChart />
        </HomeWrapper>
      )}
    </PageContainer>
  );
};

export default Home;
